import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Crown } from "lucide-react";

const GOLD = "#C9A84C";
const CALENDLY_URL = "https://calendly.com/r-cassilde";

const PHASES = [
  { r: "Phase 1", s: "S1-S3", t: "Décodage",
    d: "Triple Fusion™, calcul numérologique complet, matrice Lo Shu personnalisée." },
  { r: "Phase 2", s: "S4-S6", t: "Cartographie",
    d: "Territoires de vie, identification des blocages, contrat ancestral IFÀ." },
  { r: "Phase 3", s: "S7-S9", t: "Réalignement",
    d: "Plan d'action souverain, décrets, réorganisation Lo Shu." },
  { r: "Phase 4", s: "S10-S12", t: "Ancrage & Mission profonde",
    d: "Ancrage définitif, mission de vie, clôture souveraine." },
  { r: "Phase 5", s: "S13-S16", t: "IFÀ approfondi",
    d: "Odù personnel, Carnet d'IFÀ, rituels d'alignement." },
  { r: "Phase 6", s: "S17-S20", t: "Cycles Lo Shu",
    d: "Carte de Vie sur 9 ans, périodes favorables et pivots stratégiques." },
  { r: "Phase 7", s: "S21-S23", t: "Feng Shui personnel",
    d: "Audit espace de vie, directions Lo Shu personnalisées, réorganisation." },
  { r: "Phase 8", s: "S24", t: "Souveraineté Totale",
    d: "Séance de clôture — bilan complet, Codex Personnel, vision à 3 ans." },
];

const INCLUS = [
  "Tout le Coaching Élite (12 séances de fondation)",
  "Codex Personnel Souverain",
  "Carte de Vie sur 9 ans (Cycles Lo Shu)",
  "Carnet d'IFÀ personnel",
  "Audit Feng Shui complet",
];

const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
  >{children}</motion.div>
);

export default function CoachingSouverain() {
  useEffect(() => {
    document.title = "Coaching Souverain — Triple Fusion™ | Académie AETHERIS";
  }, []);

  return (
    <div className="min-h-screen bg-brand-ink text-white pt-24 pb-24" data-testid="coaching-souverain-page">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">

        <motion.header
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 mb-6" style={{ color: GOLD }}>
            <Crown size={18} strokeWidth={1.5} />
            <p className="text-xs uppercase tracking-[0.25em]">Programme Souverain</p>
          </div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95]">
            Coaching Souverain<br />
            <span style={{ color: GOLD }}>Triple Fusion™</span>
          </h1>
          <p className="text-white/60 text-lg md:text-xl font-light mt-8 max-w-2xl mx-auto leading-relaxed">
            Transformation intégrale sur 24 séances — autonomie vibratoire totale.
          </p>
        </motion.header>

        <section className="text-center mb-20" data-testid="tarif">
          <p className="text-xs uppercase tracking-[0.25em] mb-6" style={{ color: GOLD }}>Investissement</p>
          <div className="max-w-xl mx-auto p-10 border" style={{ borderColor: GOLD, background: "rgba(201,168,76,0.08)" }}>
            <div className="font-serif text-6xl mb-2" style={{ color: GOLD }} data-testid="tarif-prix">3 500 €</div>
            <p className="text-white/60 text-sm mb-2">24 séances individuelles · 6 mois</p>
              <p className="text-white/45 text-sm mb-6">Option : 3 200 € en paiement comptant</p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="cta-calendly"
               className="inline-flex items-center justify-center gap-3 px-10 py-5 text-xs uppercase tracking-[0.2em] font-semibold text-brand-ink transition-transform hover:scale-[1.02]"
               style={{ backgroundColor: GOLD }}>
              Réserver mon appel découverte <ArrowRight size={16} />
            </a>
            <p className="text-white/40 text-xs mt-6">Un échange de 30 minutes avec Rodrigue CASSILDÉ pour valider que ce programme est le bon pour vous.</p>
          </div>
          <p className="text-white/40 text-xs mt-8">Suivi individuel sur 24 séances</p>
        </section>

        <section className="mb-24" data-testid="phases">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>Le chemin intégral</p>
            <h2 className="font-serif text-3xl md:text-5xl mb-12">Les 8 phases du programme</h2>
          </Reveal>
          <div className="space-y-4">
            {PHASES.map((p, i) => (
              <Reveal key={p.r} delay={i * 0.04}>
                <div className="p-6 md:p-8 border border-white/10 bg-brand-surface/30 grid md:grid-cols-12 gap-6 items-center">
                  <div className="md:col-span-3">
                    <p className="font-serif text-2xl" style={{ color: GOLD }}>{p.r}</p>
                    <p className="text-xs uppercase tracking-luxe text-white/40 mt-1">{p.s}</p>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="font-serif text-xl md:text-2xl mb-2">{p.t}</h3>
                    <p className="text-white/60 font-light leading-relaxed">{p.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mb-8" data-testid="inclus">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>Ce qui est inclus</p>
            <div className="grid md:grid-cols-2 gap-4">
              {INCLUS.map((it) => (
                <div key={it} className="flex items-center gap-3 p-5 border border-white/10 bg-brand-surface/30">
                  <Check size={16} style={{ color: GOLD }} strokeWidth={2.5} />
                  <span className="text-white/80">{it}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </section>
      </div>
    </div>
  );
}
